/**
 * Eira Theme — Shoppable Videos Section
 * Infinite carousel via absolute positioning + modular index math.
 * No DOM clones. Cards are stacked and repositioned with transforms.
 */
(function () {
  'use strict';

  var activeSections = new Map();
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initShoppableVideos(sectionEl) {
    if (!sectionEl) return;

    var sectionId = sectionEl.id || 'sv';
    var track = sectionEl.querySelector('[data-sv-track]');
    var cards = Array.from(sectionEl.querySelectorAll('[data-sv-card]'));
    var prevBtn = sectionEl.querySelector('[data-sv-prev]');
    var nextBtn = sectionEl.querySelector('[data-sv-next]');
    var dots = sectionEl.querySelectorAll('[data-sv-dot]');
    var realCount = parseInt(sectionEl.dataset.svRealCount || '0', 10);
    var autoplayCenter = sectionEl.dataset.svAutoplay !== 'false';
    var showProgress = sectionEl.dataset.svProgress !== 'false';

    if (!track || cards.length === 0 || realCount === 0) return;

    var cleanupFns = [];
    var activeIndex = 0;
    var cardWidth = 0;
    var gap = 0;
    var playbackTimer = null;

    // Swipe state
    var isSwiping = false;
    var swipeStartX = 0;
    var moveListenersAttached = false;

    // How many cards to show on each side of center
    var VISIBLE_SIDE = 2;

    /* ── Helpers ────────────────────────────────────── */

    function addListener(target, event, fn, opts) {
      target.addEventListener(event, fn, opts);
      cleanupFns.push(function () { target.removeEventListener(event, fn, opts); });
    }

    function getIndex(i) {
      return ((i % realCount) + realCount) % realCount;
    }

    /* ── Measure ───────────────────────────────────── */

    function measure() {
      if (cards.length === 0) return;
      cardWidth = cards[0].offsetWidth;
      gap = parseInt(getComputedStyle(sectionEl).getPropertyValue('--sv-gap')) || 24;
    }

    /* ── Render (absolute position all cards) ──────── */

    function render() {
      var transition = prefersReducedMotion ? 'none' : '';

      // Reset all cards to hidden
      for (var i = 0; i < cards.length; i++) {
        cards[i].style.opacity = '0';
        cards[i].style.zIndex = '0';
        cards[i].style.transform = 'translateX(0)';
        cards[i].style.pointerEvents = 'none';
        cards[i].style.filter = '';
        if (transition === 'none') cards[i].style.transition = 'none';
        cards[i].classList.remove('is-centered', 'is-visible');
      }

      // Position visible cards around center
      for (var offset = -VISIBLE_SIDE; offset <= VISIBLE_SIDE; offset++) {
        var idx = getIndex(activeIndex + offset);
        var card = cards[idx];
        var absOffset = Math.abs(offset);

        // Scale decreases with distance from center
        var scale = offset === 0 ? 1 : Math.max(0.75, 1 - absOffset * 0.08);
        // Shift each card by (cardWidth + gap) * offset
        var shiftPx = offset * (cardWidth + gap);

        card.style.zIndex = String(10 - absOffset);
        card.style.opacity = '1';
        card.style.transform = 'translateX(' + shiftPx + 'px) scale(' + scale + ')';
        card.style.filter = offset === 0 ? 'none' : 'grayscale(30%)';

        if (offset === 0) {
          card.classList.add('is-centered');
          card.style.pointerEvents = 'auto';
        } else {
          card.classList.add('is-visible');
        }
      }

      syncDots();
      schedulePlayback();
    }

    /* ── Playback ──────────────────────────────────── */

    function schedulePlayback() {
      clearTimeout(playbackTimer);
      playbackTimer = setTimeout(function () {
        applyPlayback();
      }, 200);
    }

    function applyPlayback() {
      // Pause all non-active videos
      cards.forEach(function (card, i) {
        if (i === activeIndex) return;
        var video = card.querySelector('video');
        if (video && !video.paused) {
          video.pause();
          updatePlayIcon(card, false);
        }
        var bar = card.querySelector('[data-sv-progress-bar]');
        if (bar) bar.style.width = '0%';
      });

      if (!autoplayCenter) return;

      var card = cards[activeIndex];
      if (!card) return;
      var video = card.querySelector('video');
      if (!video) return;
      if (card.dataset.userPaused === 'true') return;

      if (video.paused) {
        if (video.ended) video.currentTime = 0;
        video.play().catch(function () {});
      }
      updatePlayIcon(card, true);
    }

    function updatePlayIcon(card, isPlaying) {
      var playIcon = card.querySelector('[data-sv-icon-play]');
      var pauseIcon = card.querySelector('[data-sv-icon-pause]');
      if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
      if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
    }

    /* ── Dots ──────────────────────────────────────── */

    function syncDots() {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    }

    /* ── Navigation ────────────────────────────────── */

    function next() {
      activeIndex = getIndex(activeIndex + 1);
      render();
    }

    function prev() {
      activeIndex = getIndex(activeIndex - 1);
      render();
    }

    function goTo(idx) {
      activeIndex = getIndex(idx);
      render();
    }

    /* ── Arrow & Dot Clicks ─────────────────────────── */

    if (prevBtn) addListener(prevBtn, 'click', prev);
    if (nextBtn) addListener(nextBtn, 'click', next);

    dots.forEach(function (dot) {
      addListener(dot, 'click', function () {
        goTo(parseInt(dot.dataset.svDot, 10));
      });
    });

    /* ── Keyboard Navigation ────────────────────────── */

    addListener(sectionEl, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    });

    /* ── Swipe / Drag (scoped listeners) ───────────── */

    function onPointerDown(e) {
      if (e.target.closest('button, a, [data-quick-view]')) return;
      swipeStartX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX) || 0;
      isSwiping = true;
      attachMoveListeners();
    }

    function onPointerMove(e) {
      if (!isSwiping) return;
      var currentX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX) || 0;
      var diff = currentX - swipeStartX;

      if (Math.abs(diff) > 50) {
        isSwiping = false;
        detachMoveListeners();
        if (diff < 0) next();
        else prev();
      }
    }

    function onPointerUp() {
      isSwiping = false;
      detachMoveListeners();
    }

    function attachMoveListeners() {
      if (moveListenersAttached) return;
      moveListenersAttached = true;
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('touchmove', onPointerMove, { passive: true });
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchend', onPointerUp);
    }

    function detachMoveListeners() {
      if (!moveListenersAttached) return;
      moveListenersAttached = false;
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchend', onPointerUp);
    }

    addListener(track, 'mousedown', onPointerDown);
    addListener(track, 'touchstart', onPointerDown, { passive: true });

    // Ensure cleanup removes move listeners if still attached
    cleanupFns.push(detachMoveListeners);

    /* ── Video Controls ─────────────────────────────── */

    cards.forEach(function (card, cardIndex) {
      var video = card.querySelector('video');
      var playBtn = card.querySelector('[data-sv-play]');
      var muteBtn = card.querySelector('[data-sv-mute]');
      var progressBar = card.querySelector('[data-sv-progress-bar]');

      if (!video) return;

      if (playBtn) {
        addListener(playBtn, 'click', function (e) {
          e.stopPropagation();
          if (video.paused) {
            card.dataset.userPaused = 'false';
            if (video.ended) video.currentTime = 0;
            video.play().catch(function () {});
            updatePlayIcon(card, true);
          } else {
            card.dataset.userPaused = 'true';
            video.pause();
            updatePlayIcon(card, false);
          }
        });
      }

      if (muteBtn) {
        addListener(muteBtn, 'click', function (e) {
          e.stopPropagation();
          sectionEl.querySelectorAll('video').forEach(function (v) {
            if (v !== video) v.muted = true;
          });
          sectionEl.querySelectorAll('[data-sv-mute]').forEach(function (btn) {
            if (btn !== muteBtn) {
              btn.dataset.svMuted = 'true';
              var sl = btn.querySelector('[data-sv-mute-slash]');
              if (sl) sl.style.display = '';
            }
          });
          var isMuted = muteBtn.dataset.svMuted === 'true';
          video.muted = !isMuted;
          muteBtn.dataset.svMuted = isMuted ? 'false' : 'true';
          var slash = muteBtn.querySelector('[data-sv-mute-slash]');
          if (slash) slash.style.display = isMuted ? 'none' : '';
        });
      }

      if (progressBar && showProgress) {
        addListener(video, 'timeupdate', function () {
          if (video.duration) {
            progressBar.style.width = (video.currentTime / video.duration * 100) + '%';
          }
        });
      }

      // Auto-advance when center video ends
      addListener(video, 'ended', function () {
        if (cardIndex === activeIndex) next();
      });

      // Hide poster when video plays
      var poster = card.querySelector('.bc-sv-card__poster');
      if (poster) {
        addListener(video, 'playing', function () {
          poster.classList.add('is-hidden');
        });
      }
    });

    /* ── Init ───────────────────────────────────────── */

    function init() {
      measure();
      if (cardWidth <= 0) {
        setTimeout(function () { measure(); if (cardWidth > 0) { render(); track.classList.add('sv-ready'); } }, 200);
        return;
      }
      render();
      track.classList.add('sv-ready');
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(init);
    });

    /* ── Resize ─────────────────────────────────────── */

    var resizeTimer;
    addListener(window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        measure();
        render();
      }, 150);
    });

    /* ── CSS Entrance ───────────────────────────────── */

    var entranceDone = false;

    function runEntrance() {
      if (entranceDone) return;
      entranceDone = true;
      var targets = sectionEl.querySelectorAll('[data-sv-heading], [data-sv-desc], [data-sv-nav]');
      targets.forEach(function (el) { el.classList.add('sv-entered'); });
      cards.forEach(function (c) { c.classList.add('entrance-done'); });
    }

    // Use IntersectionObserver for reliable entrance on mobile
    if ('IntersectionObserver' in window) {
      var entranceObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            runEntrance();
            entranceObs.disconnect();
          }
        });
      }, { threshold: 0.1 });
      entranceObs.observe(sectionEl);
      cleanupFns.push(function () { entranceObs.disconnect(); });
    }

    // Fallback: run on load event or immediately if already loaded
    if (document.readyState === 'complete') {
      runEntrance();
    } else {
      var lh = function () { runEntrance(); };
      window.addEventListener('load', lh);
      cleanupFns.push(function () { window.removeEventListener('load', lh); });
    }

    // Safety net: never let heading stay hidden more than 2s
    var entranceTimeout = setTimeout(runEntrance, 2000);
    cleanupFns.push(function () { clearTimeout(entranceTimeout); });

    /* ── Editor Events ──────────────────────────────── */

    addListener(document, 'shopify:block:select', function (e) {
      var blockEl = e.target.closest('[data-sv-card]');
      if (!blockEl || !sectionEl.contains(blockEl)) return;
      var idx = cards.indexOf(blockEl);
      if (idx >= 0) goTo(idx);
    });

    /* ── Cleanup ────────────────────────────────────── */

    function destroy() {
      sectionEl.querySelectorAll('video').forEach(function (v) { v.pause(); });
      cleanupFns.forEach(function (fn) { fn(); });
      cleanupFns = [];
      clearTimeout(resizeTimer);
      clearTimeout(playbackTimer);
      activeSections.delete(sectionId);
    }

    activeSections.set(sectionId, { destroy: destroy });
  }

  /* ── Bootstrap ────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('[data-sv-section]').forEach(initShoppableVideos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-sv-section]');
    if (s) initShoppableVideos(s);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var s = e.target.querySelector('[data-sv-section]');
    if (s) {
      var inst = activeSections.get(s.id || 'sv');
      if (inst) inst.destroy();
    }
  });
})();
